import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { CompanyStore } from '~/core/store/company.store';
import { StorageService } from '~/core/services/storage.service';
import { CookiePermissionService, CookieType } from '~/core/services/cookie-permission.service';
import { LoadSecondaryService } from '~/core/services/loadSecondaryService';
import { ChatVisibilityService } from '~/core/services/chat-visibility.service';
import { SportsbookIntegrationService } from '~/pages/sportsbook-integration/sportsbook-integration.service';
import { ParamsActions } from '~/core/services/navigation.service';
import { Licenses } from '~/core/constants/licenses';
import { DateUtils } from '~/core/helpers/date.utils';
import { isDesktop } from '~/core/helpers/device-utils';
import { DeviceType } from '~/core/constants/deviceType';
import { AdvertisementType } from '~/core/helpers/advertisement-utils';
import { DeferredStarterService } from '~/core/services/deferred-starter.service';

const DIALOG_DELAY_SECONDS = 10;

@Injectable({
  providedIn: 'root',
})
export class DialogOrchestratorService {
  private readonly _companyStore = inject(CompanyStore);
  private readonly _storageService = inject(StorageService);
  private readonly _dialog = inject(MatDialog);
  private readonly _route = inject(ActivatedRoute);
  private readonly _cookiePermissionService = inject(CookiePermissionService);
  private readonly _loadSecondaryService = inject(LoadSecondaryService);
  private readonly _chatVisibility = inject(ChatVisibilityService);
  private readonly _sportsbookIntegrationService = inject(SportsbookIntegrationService);
  private readonly _deferredStarter = inject(DeferredStarterService);

  showStartupDialogs(): void {
    this.showCookiesDialog();
    this.showPresentationDialog();
  }

  showMajorityDialog(): void {
    const config = this._companyStore.configSignal();
    if (!config?.confirmation_majority?.enable || this._storageService.getMajority()) {
      return;
    }

    import('~/containers/dialogs/majority-dialog/majority-dialog.component').then(({ MajorityDialogComponent }) => {
      const dialogRef = this._dialog.open(MajorityDialogComponent, {
        disableClose: true,
        autoFocus: false,
        ariaModal: true,
        ariaLabelledBy: 'dialog-majority',
        width: '370px',
        backdropClass: 'custom-dialog-overlay-dark-backdrop',
      });
      dialogRef.componentInstance.age = config?.confirmation_majority?.age;
    });
  }

  showCookiesDialog(callback?: () => void, conditional?: boolean): void {
    if (this._sportsbookIntegrationService.isIframe) {
      return;
    }

    if (!this._storageService.cookiesAllowed && this._companyStore.companySignal()?.cookies?.enable) {
      this._deferredStarter.runWhenIdle(() => {
        const currentPath = this._route.snapshot.queryParams;
        const resolvedConditional =
          conditional ?? (currentPath?.action === ParamsActions.GUEST_DEPOSIT && this._storageService.cookiesAllowed);
        const resolvedCallback = callback ?? this._loadSecondaryService.showGuestDepositModal;
        this._cookiePermissionService.openCookieDialog(resolvedCallback, resolvedConditional);
      });
    } else if (!this._storageService.cookiesAllowed) {
      this._saveCookies();
    }
  }

  showPresentationDialog(): void {
    const routesToIgnore = ['user-dashboard', 'register'];
    const shouldIgnoreRoute = routesToIgnore.some((route) => window.location.href.includes(route));

    if (shouldIgnoreRoute) {
      return;
    }

    const company = this._companyStore.companySignal();

    const shouldAlwaysShowPresentation = company?.license?.show && company?.license?.type === Licenses.BR_PARANA;
    if (shouldAlwaysShowPresentation) {
      setTimeout(() => this._openPresentationDialog(), DIALOG_DELAY_SECONDS * 1000);
      return;
    }

    const advertisements = this._companyStore.configSignal()?.advertisements || [];

    if (this._hasPresentation(advertisements)) {
      let localDate: Date;
      const now = new Date();

      if (StorageService.presentationDate) {
        localDate = new Date(StorageService.presentationDate);
      }

      if (!localDate || !DateUtils.isToday(localDate)) {
        StorageService.setPresentationDate(now.getTime());
        setTimeout(() => this._openPresentationDialog(), DIALOG_DELAY_SECONDS * 1000);
      }
    }
  }

  private _openPresentationDialog(): void {
    import('~/containers/dialogs/app-presentation/presentation.component').then(({ PresentationComponent }) => {
      this._dialog.open(PresentationComponent, {
        ariaModal: true,
        ariaLabelledBy: 'dialog-presentation',
        panelClass: 'myapp-no-background-dialog',
      });
    });
  }

  private _hasPresentation(advertisements: any[]): boolean {
    return advertisements.some((item) => {
      const hasDesktopPresentation = isDesktop() && item.device === DeviceType.DESKTOP;
      const hasMobilePresentation = !isDesktop() && item.device === DeviceType.MOBILE;
      const initialScreenImage = item.type === AdvertisementType.INITIAL_SCREEN && item.enable !== false && item.image;
      return initialScreenImage && (hasDesktopPresentation || hasMobilePresentation);
    });
  }

  private _saveCookies(): void {
    this._storageService.cookiesEssentialsAllowed = true;
    this._cookiePermissionService.setPermission(CookieType.ESSENTIALS, true);
    this._cookiePermissionService.setPermission(CookieType.ANALYTICS, true);
    this._chatVisibility.refresh();
  }
}
